'use client';
import {
  Group,
  Input,
  MultiSelect,
  Pill,
  Radio,
  rem,
  Select,
  Text,
  TextInput,
} from '@mantine/core';
import {
  Dropzone,
  MS_EXCEL_MIME_TYPE,
  MS_POWERPOINT_MIME_TYPE,
  MS_WORD_MIME_TYPE,
  PDF_MIME_TYPE,
} from '@mantine/dropzone';
import '@mantine/dropzone/styles.css';
import { useInputState } from '@mantine/hooks';
import { AssessmentQuestion, SubmissionFile } from '@scaleup/models';
import { IconFileUpload, IconUpload, IconX } from '@tabler/icons-react';
import { format } from 'date-fns';

import v from 'voca';
import { useAlert } from '../../hooks';
import { DatePickerMulti } from './DatePickerMulti';
import { InputMultiple } from './InputMultiple';
import { InputMultipleBreakout } from './InputMultipleBreakout';
import { InputMultipleFixed } from './InputMultipleFixed';
import { InputMultipleFixedLabels } from './InputMultipleFixedLabeled';
import './style.css';

interface AssessmentMultiSelectProps {
  questionId: number;
  currentValue: string;
  onAnswerChange: (
    id: number,
    value: string,
    followup_activation?: string
  ) => void;
  followupActivation?: string;
  choices: string[] | undefined;
  disabled?: boolean;
}

const AssessmentMultiSelect: React.FC<AssessmentMultiSelectProps> = ({
  questionId,
  currentValue,
  onAnswerChange,
  followupActivation,
  choices,
  disabled,
}) => {
  const [val, setVal] = useInputState(JSON.parse(currentValue.trim() || '[]'));
  const handleChange = (vals: any[]) => {
    console.log({ vals });
    onAnswerChange(questionId, JSON.stringify(vals), followupActivation);
    setVal(JSON.stringify(vals));
  };
  return (
    <MultiSelect
      className="w-full"
      onChange={handleChange}
      disabled={disabled}
      defaultValue={val}
      data={choices?.map((choice) => {
        return {
          value: choice,
          label: v.capitalize(choice),
        };
      })}
    />
  );
};

interface AssessmentQuestionInputProp {
  question: AssessmentQuestion;
  disabled?: boolean;
  getCurrentValue: (id: number) => string;
  onAnswerChange: (
    id: number,
    value: string,
    followup_activation?: string
  ) => void;
  getCurrentFile: (id: number) => SubmissionFile[] | undefined;
  removeFile: (id: number, key: string) => void;
  fileUpload: (id: number, file: File | null | undefined) => void;
  followup_activation?: string;
  className?: string;
}

export function AssessmentQuestionInput({
  question,
  disabled,
  getCurrentValue,
  onAnswerChange,
  getCurrentFile,
  removeFile,
  fileUpload,
  followup_activation,
  className,
  ...props
}: AssessmentQuestionInputProp) {
  const setAlert = useAlert();
  return (
    <div className={`${className} w-full`}>
      {question.type == 'likert' && (
        <div>
          <Radio.Group
            className="flex flex-row grow mt-5 mb-5 place-content-center"
            defaultValue={getCurrentValue(question.id)}
            onChange={(value: string) =>
              onAnswerChange(question.id, value, followup_activation)
            }
          >
            <div className="grid grid-cols-6 gap-1 text-center text-sm">
              <span className="col-span-1">
                <p className="h-[60px]">Do Not Know</p>
                <Radio
                  disabled={disabled}
                  value="Do Not Know"
                  className="flex flex-row place-content-center"
                />
              </span>
              <span>
                <p className="h-[60px]">Disagree</p>
                <Radio
                  value="Disagree"
                  className="flex flex-row place-content-center"
                  disabled={disabled}
                />
              </span>
              <span>
                <p className="h-[60px]">Somewhat Disagree</p>
                <Radio
                  value="Somewhat Disagree"
                  className="flex flex-row place-content-center"
                  disabled={disabled}
                />
              </span>
              <span>
                <p className="h-[60px]">Neither Agree nor Disagree</p>
                <Radio
                  value="Neither Agree nor Disagree"
                  className="flex flex-row place-content-center"
                  disabled={disabled}
                />
              </span>
              <span>
                <p className="h-[60px]">Somewhat Agree</p>
                <Radio
                  value="Somewhat Agree"
                  className="flex flex-row place-content-center"
                  disabled={disabled}
                />
              </span>
              <span>
                <p className="h-[60px]">Agree</p>
                <Radio
                  value="Agree"
                  className="flex flex-row place-content-center"
                  disabled={disabled}
                />
              </span>
            </div>
          </Radio.Group>
        </div>
      )}
      {question.type == 'multiple-choice' && (
        <div>
          <Select
            classNames={{ input: 'bg-[#f2f4f7] rounded-2xl' }}
            onChange={(value: string | null) => {
              if (value)
                onAnswerChange(question.id, value, followup_activation);
            }}
            defaultValue={getCurrentValue(question.id)}
            data={question.choices}
            disabled={disabled}
          ></Select>
        </div>
      )}
      {question.type == 'multiple-options' && (
        <div className="w-full">
          <AssessmentMultiSelect
            questionId={question.id}
            currentValue={getCurrentValue(question.id)}
            onAnswerChange={onAnswerChange}
            followupActivation={followup_activation}
            choices={question.choices}
            disabled={disabled}
          />
        </div>
      )}
      {question.type == 'date_time_multiple' && (
        <div className="grid grid-cols-9 gap-4 text-left my-[8px]">
          <DatePickerMulti
            count={question.count ? question.count : 2}
            onChange={(value: any[]) =>
              onAnswerChange(
                question.id,
                value
                  .map((date) => {
                    if (date == undefined) return '';
                    else return format(date, 'MM/dd/yyyy');
                  })
                  .join(','),
                followup_activation
              )
            }
            className="col-span-5"
            disabled={disabled}
            value={getCurrentValue(question.id)
              .trim()
              .split(',')
              .map((value) => {
                if (value != '') return new Date(value);
                else return undefined;
              })}
          />
        </div>
      )}
      {question.type == 'fill' && (
        <div className="grid grid-cols-9 gap-4 text-left my-[8px]">
          <Input
            onBlur={(event) =>
              onAnswerChange(
                question.id,
                event.target.value,
                followup_activation
              )
            }
            disabled={disabled}
            defaultValue={getCurrentValue(question.id)}
            className="col-span-5"
          />
        </div>
      )}
      {question.type == 'fill-multiple' && (
        <div className="grid grid-cols-9 gap-4 text-left my-[8px]">
          <InputMultiple
            onChange={(value: any[]) =>
              onAnswerChange(
                question.id,
                JSON.stringify(value),
                followup_activation
              )
            }
            defaultValue={JSON.parse(
              getCurrentValue(question.id).trim() || '[]'
            )}
            className="col-span-5"
            disabled={disabled}
          />
        </div>
      )}
      {question.type == 'fill-with-format' && (
        <div className="grid grid-cols-9 gap-4 text-left my-[8px]">
          <TextInput
            className="col-span-8"
            placeholder={question.format}
            disabled={disabled}
            onBlur={(event) =>
              onAnswerChange(
                question.id,
                event.target.value,
                followup_activation
              )
            }
            defaultValue={getCurrentValue(question.id)}
          />
        </div>
      )}
      {question.type == 'fill-multiple-with-format' && (
        <div className="grid grid-cols-9 gap-4 text-left my-[8px]">
          <InputMultiple
            className="col-span-5"
            placeholder={question.format}
            disabled={disabled}
            onChange={(value: any[]) =>
              onAnswerChange(
                question.id,
                JSON.stringify(value),
                followup_activation
              )
            }
            defaultValue={JSON.parse(
              getCurrentValue(question.id).trim() || '[]'
            )}
          />
        </div>
      )}
      {question.type == 'fill-multiple-with-format-fixed' && (
        <div className="grid grid-cols-9 gap-4 text-left my-[8px]">
          <InputMultipleFixed
            className="col-span-5"
            placeholder={question.format}
            disabled={disabled}
            onChange={(value: any) =>
              onAnswerChange(
                question.id,
                JSON.stringify(value),
                followup_activation
              )
            }
            defaultValue={JSON.parse(
              getCurrentValue(question.id).trim() || '{}'
            )}
          />
        </div>
      )}
      {question.type == 'fill-multiple-with-format-fixed-label' && (
        <div className="grid grid-cols-9 gap-4 text-left my-[8px]">
          <InputMultipleFixedLabels
            className="col-span-5"
            unavailable_text={question.unavailableText || ''}
            placeholder={question.format}
            disabled={disabled}
            labels={JSON.parse(
              getCurrentValue(question.labelAnswerId || 6) || '[]'
            )}
            onChange={(value: any) =>
              onAnswerChange(
                question.id,
                JSON.stringify(value),
                followup_activation
              )
            }
            defaultValue={JSON.parse(
              getCurrentValue(question.id).trim() || '{}'
            )}
          />
        </div>
      )}
      {question.type == 'fill-multiple-breakout-with-format' && (
        <div className="grid grid-cols-9 gap-4 text-left my-[8px]">
          {
            <InputMultipleBreakout
              className="col-span-5"
              placeholders={question.formatBreakout}
              disabled={disabled}
              onChange={(value: any) =>
                onAnswerChange(
                  question.id,
                  JSON.stringify(value),
                  followup_activation
                )
              }
              defaultValue={(() => {
                try {
                  const parsed = JSON.parse(getCurrentValue(question.id).trim()).filter(
                    (n: string[][]) => {
                      return n.reduce((prev, curr) => prev || curr);
                    }
                  );
                  return parsed.length > 0 ? parsed : [[]];
                } catch (e) {
                  return [[]];
                }
              })()}
            />
          }
        </div>
      )}
      {question.type == 'yes-no' && (
        <div className="items-center">
          <Radio.Group
            className="flex flex-row grow mt-5 mb-5 place-content-center"
            defaultValue={getCurrentValue(question.id)}
            onChange={(value: string) =>
              onAnswerChange(question.id, value, followup_activation)
            }
          >
            <Group mt="xs">
              <Radio value="Yes" label="Yes" disabled={disabled} />
              <Radio value="No" label="No" disabled={disabled} />
              <Radio
                value="Do Not know"
                label="Do Not Know"
                disabled={disabled}
              />
            </Group>
          </Radio.Group>
        </div>
      )}
      {question.documentation && (
        <div className="text-left my-[8px] py-4 pr-2">
          <span className="font-medium text-[14px] leading-[30px]">
            If available, please upload supporting documentation for this
            answer. Examples include: {question.documentation_desc}.
          </span>
          {getCurrentFile(question.id) && (
            <div className={`w-full my-10 ml-2`}>
              {getCurrentFile(question.id)?.map((file: SubmissionFile) => {
                return (
                  <Pill
                    key={file.fileName}
                    className="mr-3 mb-2"
                    onRemove={() => removeFile(question.id, file.fileName)}
                    withRemoveButton
                  >
                    {file.fileName}
                  </Pill>
                );
              })}
            </div>
          )}
          <Dropzone
            className="bg-[#EDF7FA]"
            onDrop={(files) => fileUpload(question.id, files[0])}
            disabled={disabled}
            onReject={(files) => {
              setAlert({
                severity: 'error',
                title: 'File Upload',
                message: `${files[0]?.file.name} couldn't be uploaded.`,
                show: true,
              });
            }}
            maxSize={5 * 1024 ** 2}
            accept={[
              ...MS_EXCEL_MIME_TYPE,
              ...MS_POWERPOINT_MIME_TYPE,
              ...MS_WORD_MIME_TYPE,
              ...PDF_MIME_TYPE,
            ]}
            {...props}
          >
            <Group
              justify="center"
              gap="xl"
              mih={120}
              style={{ pointerEvents: 'none' }}
            >
              <Dropzone.Accept>
                <IconUpload
                  style={{
                    width: rem(52),
                    height: rem(52),
                    color: 'var(--mantine-color-blue-6)',
                  }}
                  stroke={1.5}
                />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX
                  style={{
                    width: rem(52),
                    height: rem(52),
                    color: 'var(--mantine-color-red-6)',
                  }}
                  stroke={1.5}
                />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconFileUpload
                  style={{
                    width: rem(52),
                    height: rem(52),
                    color: '#377D91',
                  }}
                />
              </Dropzone.Idle>

              <div>
                <Text size="m" inline className="text-center">
                  <span className="font-bold text-[#377D91]">
                    Click to upload
                  </span>{' '}
                  or drag and drop
                </Text>
                <Text
                  size="sm"
                  c="dimmed"
                  inline
                  mt={7}
                  className="text-center"
                >
                  .pdf, .doc, .xls, .ppt (Max file size 5 MB)
                </Text>
              </div>
            </Group>
          </Dropzone>
        </div>
      )}
    </div>
  );
}
